export const BG_VERTEX_SRC = `
attribute vec2 a_position;
varying vec2 v_uv;
void main() {
    v_uv = a_position * 0.5 + 0.5;
    gl_Position = vec4(a_position, 0.0, 1.0);
}
`;

export const BG_FRAGMENT_SRC = `
precision highp float;
uniform float u_time;
uniform float u_zoom;
uniform vec4 u_worldBounds;
uniform vec4 u_mapTint;
varying vec2 v_uv;

// Sam Hocevar RGB<->HSV — cheap, branch-free.
vec3 rgb2hsv(vec3 c) {
    vec4 K = vec4(0.0, -1.0 / 3.0, 2.0 / 3.0, -1.0);
    vec4 p = mix(vec4(c.bg, K.wz), vec4(c.gb, K.xy), step(c.b, c.g));
    vec4 q = mix(vec4(p.xyw, c.r), vec4(c.r, p.yzx), step(p.x, c.r));
    float d = q.x - min(q.w, q.y);
    return vec3(abs(q.z + (q.w - q.y) / (6.0 * d + 1e-10)), d / (q.x + 1e-10), q.x);
}
vec3 hsv2rgb(vec3 c) {
    vec4 K = vec4(1.0, 2.0 / 3.0, 1.0 / 3.0, 3.0);
    vec3 p = abs(fract(c.xxx + K.xyz) * 6.0 - K.www);
    return c.z * mix(K.xxx, clamp(p - K.xxx, 0.0, 1.0), c.y);
}

float hash(vec2 p) {
    p = fract(p * vec2(123.34, 456.21));
    p += dot(p, p + 45.32);
    return fract(p.x * p.y);
}
float valueNoise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(
        mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x),
        mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x),
        u.y
    );
}
// Linear drift + slow orbital wobble — shapes evolve rather than just translate.
float fbm(vec2 p, float t) {
    vec2 orbit = vec2(sin(t * 0.09), cos(t * 0.09)) * 0.45;
    float n = 0.0;
    n += 0.65 * valueNoise(p * 1.0 + vec2(t * 0.055, t * 0.040) + orbit);
    n += 0.35 * valueNoise(p * 2.1 - vec2(t * 0.085, t * 0.065) + orbit * 0.6);
    return n;
}

void main() {
    vec2 worldPos = vec2(
        mix(u_worldBounds.x, u_worldBounds.z, v_uv.x),
        mix(u_worldBounds.y, u_worldBounds.w, v_uv.y)
    );

    vec2 pFine  = worldPos * (1.0 / 1000.0);
    vec2 pMid   = worldPos * (1.0 / 20000.0);
    vec2 pBroad = worldPos * (1.0 / 500000.0);
    vec2 pUltra = worldPos * (1.0 / 4000000.0);

    float timeScale = 1.0 + u_zoom * 0.04;
    float t = u_time * timeScale;

    float wFine  = exp(-pow((u_zoom - 14.0) / 2.5, 2.0));
    float wMid   = exp(-pow((u_zoom - 10.0) / 2.5, 2.0));
    float wBroad = exp(-pow((u_zoom -  6.0) / 2.5, 2.0));
    float wUltra = exp(-pow((u_zoom -  1.0) / 3.0, 2.0));
    float wSum   = max(wFine + wMid + wBroad + wUltra, 0.001);

    // Skip FBM evaluations for octaves with negligible weight — each FBM call
    // is ~8 hash + mix ops, so skipping saves significant ALU on mobile GPUs.
    float THRESHOLD = 0.01;

    vec2 qFine  = vec2(0.0);
    vec2 qMid   = vec2(0.0);
    vec2 qBroad = vec2(0.0);
    vec2 qUltra = vec2(0.0);
    float nFine  = 0.0;
    float nMid   = 0.0;
    float nBroad = 0.0;
    float nUltra = 0.0;

    if (wFine > THRESHOLD) {
        qFine  = vec2(fbm(pFine,  t),       fbm(pFine  + vec2(5.2, 1.3), t));
        nFine  = fbm(pFine  + 1.2 * qFine,  t);
    }
    if (wMid > THRESHOLD) {
        qMid   = vec2(fbm(pMid,   t * 0.7), fbm(pMid   + vec2(5.2, 1.3), t * 0.7));
        nMid   = fbm(pMid   + 1.2 * qMid,   t * 0.7);
    }
    if (wBroad > THRESHOLD) {
        qBroad = vec2(fbm(pBroad, t * 0.5), fbm(pBroad + vec2(5.2, 1.3), t * 0.5));
        nBroad = fbm(pBroad + 1.2 * qBroad, t * 0.5);
    }
    if (wUltra > THRESHOLD) {
        qUltra = vec2(fbm(pUltra, t * 0.35), fbm(pUltra + vec2(5.2, 1.3), t * 0.35));
        nUltra = fbm(pUltra + 1.2 * qUltra, t * 0.35);
    }

    float n = (wFine * nFine + wMid * nMid + wBroad * nBroad + wUltra * nUltra) / wSum;

    // Wisp layer — pick the dominant octave's warped coords
    vec2 pWisp = pUltra + 1.2 * qUltra;
    pWisp = mix(pWisp, pBroad + 1.2 * qBroad, smoothstep(2.5, 6.0, u_zoom));
    pWisp = mix(pWisp, pMid   + 1.2 * qMid,   smoothstep(7.0, 11.0, u_zoom));
    pWisp = mix(pWisp, pFine  + 1.2 * qFine,  smoothstep(11.0, 14.0, u_zoom));
    float wisp = fbm(pWisp * 0.55 + vec2(t * 0.15, -t * 0.11), t * 0.40);

    n = smoothstep(0.12, 0.88, n);
    wisp = smoothstep(0.30, 0.90, wisp);

    // Default pastel-holographic palette (fallback for achromatic / missing tint).
    float warmth = smoothstep(6.0, 16.0, u_zoom);
    vec3 darkFog    = mix(vec3(0.08, 0.08, 0.14), vec3(0.11, 0.09, 0.15), warmth);
    vec3 midFog     = mix(vec3(0.22, 0.22, 0.34), vec3(0.28, 0.22, 0.36), warmth);
    vec3 hiFog      = mix(vec3(0.52, 0.48, 0.72), vec3(0.62, 0.48, 0.68), warmth);
    vec3 flareColor = vec3(0.86, 0.72, 0.96);

    // Hybrid tint palette: analogous spread across dark/mid/hi (±20° hue) with a
    // complementary (+180°) flare reserved for the brightest wisp cores. Grey
    // tints (s≈0) fade back into the pastel fallback via tintWeight.
    if (u_mapTint.a > 0.0) {
        vec3 hsv = rgb2hsv(u_mapTint.rgb);
        float tintWeight = u_mapTint.a * smoothstep(0.04, 0.22, hsv.y);
        float h = hsv.x;
        float v = max(hsv.z, 0.12);
        float sDark  = clamp(hsv.y * 0.85, 0.25, 0.85);
        float sMid   = clamp(hsv.y * 1.00, 0.35, 0.90);
        float sHi    = clamp(hsv.y * 0.75, 0.20, 0.80);
        float sFlare = clamp(hsv.y * 0.90, 0.45, 0.90);

        vec3 tintedDark  = hsv2rgb(vec3(fract(h - 0.055), sDark,  v * 0.55));
        vec3 tintedMid   = hsv2rgb(vec3(h,                 sMid,   v * 1.00));
        vec3 tintedHi    = hsv2rgb(vec3(fract(h + 0.055),  sHi,    min(v * 1.70, 0.95)));
        vec3 tintedFlare = hsv2rgb(vec3(fract(h + 0.500),  sFlare, 0.90));

        darkFog    = mix(darkFog,    tintedDark,  tintWeight);
        midFog     = mix(midFog,     tintedMid,   tintWeight);
        hiFog      = mix(hiFog,      tintedHi,    tintWeight);
        flareColor = mix(flareColor, tintedFlare, tintWeight);
    }

    vec3 fogColor = mix(darkFog, midFog, n);
    fogColor = mix(fogColor, hiFog, wisp * 0.70);
    float flareMask = smoothstep(0.72, 0.98, wisp) * smoothstep(0.45, 0.90, n);
    fogColor = mix(fogColor, flareColor, flareMask * 0.30);

    vec2 c = v_uv - 0.5;
    float vignette = 1.0 - smoothstep(0.35, 0.85, length(c));
    fogColor *= mix(0.85, 1.0, vignette);

    // Premultiplied alpha — composite pass un-premultiplies to straight for MapLibre.
    // 0.90 = ~10% of the underlying map shows through in unrevealed areas.
    float alpha = 0.90;
    gl_FragColor = vec4(fogColor * alpha, alpha);
}
`;

export const ERASE_FRAGMENT_SRC = `
precision highp float;
uniform sampler2D u_atlas;
uniform vec4 u_worldBounds;
uniform vec4 u_atlasOrigin;
uniform float u_tileSize;
varying vec2 v_uv;
void main() {
    vec2 worldPos = vec2(
        mix(u_worldBounds.x, u_worldBounds.z, v_uv.x),
        mix(u_worldBounds.y, u_worldBounds.w, v_uv.y)
    );
    vec2 tileCoord = worldPos / u_tileSize;
    vec2 atlasUV = (tileCoord - u_atlasOrigin.xy) / u_atlasOrigin.zw;
    if (atlasUV.x < 0.0 || atlasUV.x > 1.0 || atlasUV.y < 0.0 || atlasUV.y > 1.0) discard;
    float b = texture2D(u_atlas, atlasUV).r;
    float revealed = smoothstep(0.2, 0.8, b);
    gl_FragColor = vec4(0.0, 0.0, 0.0, revealed);
}
`;

export const COMP_FRAGMENT_SRC = `
precision highp float;
uniform sampler2D u_tex;
varying vec2 v_uv;
void main() {
    vec4 t = texture2D(u_tex, v_uv);
    float a = t.a;
    vec3 rgb = a > 1e-4 ? t.rgb / a : vec3(0.0);
    // Straight alpha for MapLibre translucent pass (matches ColorMode.alphaBlended).
    gl_FragColor = vec4(rgb, a);
}
`;
